export interface CapacityWarningInfo {
  title: string;
  message: string;
  confirmLabel: string;
}

export interface AvailableTimeOption {
  key: string;
  value: string;
  time: string;
  turno: number | null;
  horario_id: number | null;
  isBlocked: boolean;
  label: string;
}

export const toNumberOrNull = (value: any): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

export const isCanceledStatus = (status?: string | null) => {
  const normalized = status?.toLowerCase?.().trim?.() || '';
  return normalized === 'cancelado' || normalized === 'cancelada';
};

export const extractReservationTableIds = (reservation?: any): string[] => {
  if (!reservation) return [];

  const rawMesaIds = Array.isArray(reservation?.mesa_ids) && reservation.mesa_ids.length > 0
    ? reservation.mesa_ids
    : (reservation?.mesa_id !== null && reservation?.mesa_id !== undefined && reservation?.mesa_id !== ''
      ? [reservation.mesa_id]
      : []);

  return rawMesaIds
    .map((mesaId: string | number) => String(mesaId).trim())
    .filter(Boolean);
};

export const formatDateKey = (date: Date | null | undefined) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const getGuestCapacityWarning = (message: string): CapacityWarningInfo | null => {
  const normalizedMessage = message
    ? message.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    : '';

  if (!normalizedMessage.includes('numero minimo')) {
    return null;
  }

  const isMesaWarning = normalizedMessage.includes('mesa');
  return {
    title: isMesaWarning ? 'Grupo abaixo do mínimo da mesa' : 'Grupo abaixo do mínimo do ambiente',
    message,
    confirmLabel: isMesaWarning
      ? 'Estou de acordo em seguir com a quantidade abaixo do mínimo configurado para a mesa.'
      : 'Estou de acordo em seguir com a quantidade abaixo do mínimo configurado para o ambiente.'
  };
};

export const collectBlockedSets = (
  bloqueios: any[] | null | undefined,
  formattedDate: string,
  salaId: string
) => {
  const blockedTurnoSet = new Set<number>();
  const blockedHorarioSet = new Set<number>();

  (bloqueios || []).forEach((block) => {
    if (!block) return;
    const matchesDate = block.data === formattedDate;
    const matchesSala = !block.sala_id || String(block.sala_id) === String(salaId);
    if (!matchesDate || !matchesSala) return;

    if (block.turno !== null && block.turno !== undefined) {
      const parsedTurno = Number(block.turno);
      if (!Number.isNaN(parsedTurno)) blockedTurnoSet.add(parsedTurno);
    }
    if (block.horario_id !== null && block.horario_id !== undefined) {
      const parsedHorario = Number(block.horario_id);
      if (!Number.isNaN(parsedHorario)) blockedHorarioSet.add(parsedHorario);
    }
  });

  return { blockedTurnoSet, blockedHorarioSet };
};

export const buildAvailableTimeOptions = (
  shiftMap: Record<string, any> | null | undefined,
  blockedTurnoSet: Set<number>,
  blockedHorarioSet: Set<number>
): AvailableTimeOption[] => {
  const shiftEntries = Object.entries(shiftMap || {}).sort(([firstKey], [secondKey]) => {
    const first = Number(firstKey);
    const second = Number(secondKey);
    if (Number.isNaN(first) || Number.isNaN(second)) return firstKey.localeCompare(secondKey);
    return first - second;
  });

  const builtTimeOptions: AvailableTimeOption[] = [];
  shiftEntries.forEach(([turnoKey, data]) => {
    const turnoNumber = Number(turnoKey);
    const rawHorarioId = data?.horario_id !== undefined && data?.horario_id !== null
      ? Number(data.horario_id)
      : null;
    const horarioId = Number.isFinite(rawHorarioId) ? rawHorarioId : null;
    const horariosList = Array.isArray(data?.horarios) ? data.horarios : [];

    horariosList.forEach((time: string, index: number) => {
      const normalizedTime = String(time || '').trim();
      if (!normalizedTime) return;

      const isBlocked = (
        !Number.isNaN(turnoNumber) && blockedTurnoSet.has(turnoNumber)
      ) || (
        horarioId !== null && blockedHorarioSet.has(horarioId)
      );

      builtTimeOptions.push({
        key: `${turnoKey}-${horarioId ?? 'none'}-${normalizedTime}-${index}`,
        value: `${normalizedTime}|${horarioId ?? ''}|${!Number.isNaN(turnoNumber) ? turnoNumber : ''}`,
        time: normalizedTime,
        turno: Number.isNaN(turnoNumber) ? null : turnoNumber,
        horario_id: horarioId,
        isBlocked,
        label: normalizedTime,
      });
    });
  });

  const timeCounter = new Map<string, number>();
  builtTimeOptions.forEach((option) => {
    timeCounter.set(option.time, (timeCounter.get(option.time) || 0) + 1);
  });

  const timeOrderCounter = new Map<string, number>();
  return builtTimeOptions.map((option) => {
    const total = timeCounter.get(option.time) || 0;
    if (total <= 1) return option;

    const sequence = (timeOrderCounter.get(option.time) || 0) + 1;
    timeOrderCounter.set(option.time, sequence);
    return {
      ...option,
      label: `${option.time} (intervalo ${sequence})`,
    };
  });
};

export type PickInitialTimeOptionArgs = {
  keepCurrentSelection?: boolean;
  preselectedTime?: string | null;
  preselectedHorarioId?: number | string | null;
};

export const pickInitialTimeOption = (
  options: AvailableTimeOption[],
  {
    keepCurrentSelection = false,
    preselectedTime,
    preselectedHorarioId,
  }: PickInitialTimeOptionArgs = {}
): AvailableTimeOption | null => {
  if (!options.length) return null;

  const normalizedPreselectedHorario =
    preselectedHorarioId !== undefined && preselectedHorarioId !== null
      ? Number(preselectedHorarioId)
      : null;

  let selectedOption: AvailableTimeOption | null = null;

  if (keepCurrentSelection) {
    if (
      preselectedTime
      && normalizedPreselectedHorario !== null
      && !Number.isNaN(normalizedPreselectedHorario)
    ) {
      selectedOption = options.find((option) => (
        option.time === preselectedTime
        && option.horario_id !== null
        && option.horario_id === normalizedPreselectedHorario
      )) || null;
    }

    if (!selectedOption && preselectedTime) {
      selectedOption = options.find((option) => option.time === preselectedTime) || null;
    }

    if (
      !selectedOption
      && normalizedPreselectedHorario !== null
      && !Number.isNaN(normalizedPreselectedHorario)
    ) {
      selectedOption = options.find((option) => (
        option.horario_id !== null
        && option.horario_id === normalizedPreselectedHorario
      )) || null;
    }
  }

  if (!selectedOption) {
    selectedOption = options.find((option) => !option.isBlocked) || options[0] || null;
  }

  return selectedOption;
};

export const extractErrorMessage = (error: any, defaultMessage: string) => {
  let errorMessage = defaultMessage;

  if (error?.response?.data) {
    errorMessage = error.response.data.message ||
      error.response.data.error ||
      error.response.data.msg ||
      (typeof error.response.data === 'string' ? error.response.data : null) ||
      errorMessage;
  } else if (error?.message) {
    errorMessage = error.message;
  }

  return errorMessage;
};

export type GuestValidationTable = {
  id: string | number;
  name: string;
  minCovers?: number | string | null;
  maxCovers?: number | string | null;
};

export const validateGuestCount = ({
  salaInfo,
  salaUsaMesas,
  guests,
  tableIds,
  allTables,
  fallbackTableIds = [],
}: {
  salaInfo: any;
  salaUsaMesas: boolean;
  guests: number;
  tableIds?: Array<string | number>;
  allTables: GuestValidationTable[];
  fallbackTableIds?: Array<string | number>;
}): string => {
  if (!salaInfo) return '';

  const roomMinLimit = toNumberOrNull(salaInfo.min_pessoas_por_reserva);
  const roomMaxLimit = toNumberOrNull(salaInfo.max_pessoas_por_reserva);
  const minLimit = roomMinLimit ?? 1;
  const maxLimit = roomMaxLimit ?? 20;

  if (roomMinLimit !== null && guests < roomMinLimit) {
    return `Número mínimo de pessoas: ${minLimit}`;
  }
  if (roomMaxLimit !== null && guests > roomMaxLimit) {
    return `Número máximo de pessoas: ${maxLimit}`;
  }

  if (!salaUsaMesas) {
    if (guests > maxLimit) {
      return `Número máximo de pessoas: ${maxLimit}`;
    }
  }

  if (salaUsaMesas) {
    const selectedTableIds = Array.isArray(tableIds) && tableIds.length > 0
      ? tableIds.map((tableId) => String(tableId))
      : fallbackTableIds.map((tableId) => String(tableId));
    if (selectedTableIds.length === 0) return '';

    const selectedTables = allTables.filter((table) => (
      selectedTableIds.some((tableId) => String(table.id) === String(tableId))
    ));
    if (selectedTables.length === 0) return '';

    if (selectedTables.length === 1) {
      const [selectedTable] = selectedTables;
      const minMesa = toNumberOrNull(selectedTable.minCovers);

      if (minMesa !== null && guests < minMesa) {
        return `Número mínimo de pessoas para a mesa ${selectedTable.name}: ${minMesa}`;
      }
    }

    if (selectedTables.length > 1) {
      const maxCapacities = selectedTables
        .map((table) => toNumberOrNull(table.maxCovers))
        .filter((capacity): capacity is number => capacity !== null);
      const totalCapacity = maxCapacities.reduce((total, capacity) => total + capacity, 0);

      if (maxCapacities.length === selectedTables.length && guests > totalCapacity) {
        return `Número máximo de pessoas para as mesas selecionadas: ${totalCapacity}`;
      }
    }
  }

  return '';
};

export type ReservationFormDataShape = {
  date: Date;
  time: string;
  guests: number;
  name: string;
  phone: string;
  email: string;
  birthdate: string;
  repeat: boolean;
  tableId: string;
  tableIds: string[];
  sala_id: string;
  turno: number | undefined;
  horario_id: string | number;
  notes: string;
};

export const buildReservationFormData = ({
  reservation,
  selectedDate,
  preselectedSalaId = '',
}: {
  reservation?: any;
  selectedDate: Date;
  preselectedSalaId?: string;
}): ReservationFormDataShape => {
  if (reservation) {
    const apiTime = reservation.hora ? reservation.hora.split(':').slice(0, 2).join(':') : '';
    const reservationTableIds = extractReservationTableIds(reservation);
    return {
      date: new Date(reservation.data + 'T00:00:00'),
      time: apiTime,
      guests: reservation.numero_pessoas ?? 2,
      name: reservation.nome_cliente || '',
      phone: reservation.telefone_cliente || '',
      email: reservation.email_cliente || '',
      birthdate: reservation.nascimento_cliente
        ? reservation.nascimento_cliente.split('T')[0]
        : '',
      repeat: false,
      tableId: reservationTableIds[0] ?? '',
      tableIds: reservationTableIds,
      sala_id: reservation.sala_id ?? '',
      turno: reservation.turno ?? undefined,
      horario_id: reservation.horario_id ?? '',
      notes: reservation.obs || '',
    };
  }

  return {
    date: selectedDate,
    time: '',
    guests: 2,
    name: '',
    phone: '',
    email: '',
    birthdate: '',
    repeat: false,
    tableId: '',
    tableIds: [],
    sala_id: preselectedSalaId,
    turno: undefined,
    horario_id: '',
    notes: '',
  };
};

export const RESERVATION_SAVE_VALIDATION_ERRORS = [
  'Mesa ocupada neste horário',
  'Número mínimo de pessoas',
  'Número de pessoas deve ser entre',
  'Sem mais vagas disponíveis',
  'Horário inválido',
];

export const isReservationSaveValidationError = (errorMessage: string) => (
  RESERVATION_SAVE_VALIDATION_ERRORS.some((msg) => errorMessage.includes(msg))
);

export const getCrmSearchErrorMessage = (error: any) => {
  if (error?.response?.status === 403) {
    return 'O CRM não está disponível para este usuário.';
  }
  return error?.response?.data?.error || 'Não foi possível buscar clientes no CRM.';
};

export const buildReservationPayload = ({
  formData,
  guestCount,
  salaUsaMesas,
  selectedTableIds,
  turno,
  horarioId,
  guestCapacityWarningConfirmed,
  requiresRestaurantConfirmation,
  isEditing,
}: {
  formData: ReservationFormDataShape;
  guestCount: number;
  salaUsaMesas: boolean;
  selectedTableIds: string[];
  turno: number | null;
  horarioId: number | null;
  guestCapacityWarningConfirmed: boolean;
  requiresRestaurantConfirmation: boolean;
  isEditing: boolean;
}) => {
  const reservationData: any = {
    data: formData.date.toLocaleDateString('en-CA'),
    hora: formData.time,
    numero_pessoas: guestCount,
    nome_cliente: formData.name,
    telefone_cliente: formData.phone,
    email_cliente: formData.email.trim() ? formData.email.trim() : null,
    nascimento_cliente: formData.birthdate || null,
    mesa_id: salaUsaMesas ? (selectedTableIds[0] ?? null) : null,
    mesa_ids: salaUsaMesas ? selectedTableIds : [],
    sala_id: formData.sala_id,
    turno,
    horario_id: horarioId,
    obs: formData.notes,
    ...(guestCapacityWarningConfirmed ? { capacidade_excedida_confirmada: true } : {}),
  };

  if (!isEditing && requiresRestaurantConfirmation) {
    reservationData.status = 'pendente';
  }

  return reservationData;
};
